// Importing useState hook and FormEvent type from React for state management and form event typing
import { useState, type FormEvent } from 'react';
// Importing useNavigate hook from react-router-dom to programmatically navigate between routes
import { useNavigate } from 'react-router-dom';
// Importing custom hook useAuthContext to access authentication context values and functions
import { useAuthContext } from '../../context/AuthContextUtils';
// Importing a pre-configured Axios instance for making HTTP requests to the backend API
import axiosClient from '../../api/axiosClient';

function Login() {
  // Form state for username and password
  const [formData, setFormData] = useState({ username: '', password: '' });
  // Error state for displaying login or CSRF errors
  const [error, setError] = useState<string | null>(null);
  // AuthContext functions and loading state
  const { login, isLoading } = useAuthContext();
  const navigate = useNavigate();
  // Function to fetch CSRF token from backend
  const getCsrfToken = async () => {
    try {
      const response = await axiosClient.get('/elearning/get-csrf/', { withCredentials: true });
      return response.data.csrfToken;
    } catch (err: any) {
      console.error('[Login] Error fetching CSRF token:', err.response?.data || err.message);
      setError('Failed to fetch CSRF token');
      return null;
    }
  };
  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    setError(null);
    // Get CSRF token before sending login request
    const csrfToken = await getCsrfToken();
    if (!csrfToken) return; // Stop if CSRF token fetch fails
    try {
      // Send login request to backend
      await axiosClient.post(
        '/elearning/api/login/',
        { username: formData.username, password: formData.password },
        {
          headers: {
            'X-CSRFToken': csrfToken,        // Include CSRF token
            'Content-Type': 'application/json',
          },
          withCredentials: true,            // Send session cookie
        }
      );
      // Update AuthContext with login
      await login(formData.username, formData.password);
      // Redirect to home page on successful login
      navigate('/');
    } catch (err: any) {
      console.error('[Login] Login error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    }
  };
  return (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
      {/* Page heading */}
      <h1 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">
        Login
      </h1>

      {/* Display error messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Login form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <input
          type="text"
          name="username"
          placeholder="Username"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          required
          autoComplete="username"
          disabled={isLoading}
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          required
          autoComplete="current-password"
          disabled={isLoading}
        />

        {/* Action buttons */}
        <div className="flex flex-col gap-3 mt-2">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full text-white font-medium rounded-lg p-3 shadow ${
              isLoading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition'
            }`}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/signup')}
            disabled={isLoading}
            className={`w-full text-white font-medium rounded-lg p-3 shadow ${
              isLoading ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 transition'
            }`}
          >
            Signup
          </button>
        </div>
      </form>
    </div>
  </div>
);


}
export default Login;